import { useLayoutContext } from "@/contexts/layout-context";
import { offeredOn } from "@/utils/list-forms";
import { ListView, ListViewChart, ListViewOrderBy, ListViewService } from "@/services/list-view";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ObjectLayoutProps } from "../layout-2/types2";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { ObjectListFilters2 } from "./object-list-filters-2";
import { FilterTreeDialog } from "./filter-tree/filter-tree-dialog";
import { FilterNodeSummary } from "./filter-tree/filter-tree-summary";
import { areTreesEqual } from "./filter-tree/tree-utils";
import type { FilterTree } from "./filter-tree/types";
import { DataService } from "@/services/data";

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useListViews } from "@/hooks/use-list-views";
import { cn } from "@/lib/utils";
import { CreateListViewData, UpdateListViewData } from "@/services/list-view";
import { PaginatedResponse } from "@/types/common";
import { DRQBuilder } from "@/utils/drq";
import { ChevronDown, Columns3Cog, Funnel, Loader2, Pencil, RotateCw, RulerDimensionLine, Save, SaveAll, Settings, Share2 } from "lucide-react";
import { Await, Location, useLocation, useNavigate, useNavigation, useRevalidator, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Actions2 } from "./actions-2";
import { ColumnConfigModal } from "./column-config-modal";
import { RenameViewModal } from "./create-edit-view-modal";
import { DeleteViewModal } from "./delete-view-modal";
import { ObjectListPagination2 } from "./object-list-pagination-2";
import { ObjectListSearch2 } from "./object-list-search-2";
import { ObjectListTableResizable } from "./object-list-table-resizable-2";
import { SaveAsViewModal } from "./save-as-view-modal";
import { SortControl } from "./sort-control";
import { ShareViewModal } from "./share-view-modal";
import { ViewsSelectorComponent } from "./views-selector";

// Extracted hooks and utilities
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { useDataFetching } from "@/hooks/use-data-fetching";
import { useObjectListFilters } from "@/hooks/use-object-list-filters";
import { RECENTLY_VIEWED_VIEW_ID, buildRecentlyViewedView, useObjectListViews } from "@/hooks/use-object-list-views";
import { LAYOUT_AUDIENCES, offersSavedViews } from "@/lib/navigation";
import { useUser } from "@/providers/user";
import { RecentRecordService } from "@/services/recent-record";
import { OptionsResponse } from "@/types/dynamic-detail";
import { generateAutoFilters, getFilteredColumns, mergeColumns } from "@/utils/object-list-generators";
import * as LucideIcons from "lucide-react";
import { Filter } from "../layout/types";
import { Skeleton } from "../ui/skeleton";
import { DynamicObjectForm } from "./dynamic-object-form";
import { FormChooser, type OfferedForm } from "./form-chooser";
import { ListViewChartsPanel } from "./list-view-charts-panel";
import { readContextParams } from "@/utils/form-conditions";

interface ObjectListLayout2Props<T> extends ObjectLayoutProps {
    onRowNavigateTo?: (instance: T, currentLocation: Location) => string | Map<string, string>;
    links?: React.ReactNode[];
    allowAutoRedirect?: boolean;
    allowBatchSelection?: boolean;
    onSelectionChange?: (items: T[]) => void;
    itemToId?: (item: T) => string;
    allowColumnResizing?: boolean;
    allowColumnOrdering?: boolean;
    allowLateralScroll?: boolean;
    layout?: "default" | "dense";
    primary_column: string;
    endpointUrl: string;
    /**
     * Name of this list page (`FormConfig.list_pages`): it offers exactly the creation forms the server
     * declares for that name. Unset: the object's main list page (`"default"`).
     */
    listPage?: string;
    autoFetch?: boolean;
    metadata?: OptionsResponse | null;
}

// Fonction pour normaliser les objets (trier les clés récursivement)
function normalizeObject(obj: any): any {
    if (Array.isArray(obj)) {
        return obj.map(normalizeObject);
    } else if (obj && typeof obj === "object") {
        return Object.keys(obj)
            .sort()
            .reduce((acc, key) => {
                acc[key] = normalizeObject(obj[key]);
                return acc;
            }, {} as any);
    }
    return obj;
}
// Comparaison profonde des objets DRQ (filtres), indépendante de l'ordre des clés
function areDRQObjectsEqual(a: any, b: any) {
    const normA = normalizeObject(a || {});
    const normB = normalizeObject(b || {});
    const result = JSON.stringify(normA) === JSON.stringify(normB);
    // console.log("[DRQ Compare] normA:", normA, "normB:", normB, "equal:", result);
    return result;
}
// Comparaison stricte de deux tableaux (ordre et contenu)
function areArraysEqual(a: string[] = [], b: string[] = []) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
}

function ObjectListContent<T>(props: ObjectListLayout2Props<T> & { resolvedData?: PaginatedResponse<T> }) {
    const { user, audience, is_visible_to_audience } = useUser();
    const { setShowUtilityBarSpace } = useLayoutContext();
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const revalidator = useRevalidator();
    const [searchParams, setSearchParams] = useSearchParams();

    // Global loading state
    const [globalLoading, setGlobalLoading] = useState(true);

    // A form flagged `create_from_list: false` only exists as the write contract of another surface
    // (a related list tab, which injects fields this page cannot fill), or belongs to another
    // audience (served empty), so it opens no dialog here. Several offered forms open a chooser
    // first (`chooser_card`, G2).
    const offeredForms: OfferedForm[] = (props.metadata?.list_config?.forms ?? [])
        .map((form, index) => ({ form, index }))
        .filter(({ form }) => form.create_from_list !== false && offeredOn(form, props.listPage));
    const [chosenForm, setChosenForm] = useState<OfferedForm | null>(null);
    const [chooserOpen, setChooserOpen] = useState(false);
    const createForm = chosenForm ?? (offeredForms.length === 1 ? offeredForms[0] : null);
    const [createFormOpen, setCreateFormOpenState] = useState(false);
    const setCreateFormOpen = (open: boolean) => {
        setCreateFormOpenState(open);
        if (!open && offeredForms.length > 1) setChosenForm(null);
        // A form opened by `?create=` leaves the URL once closed, its context parameters with it, in place
        // (`replace`): going back to the list does not open it again.
        if (!open && searchParams.has("create")) {
            const next = new URLSearchParams(searchParams);
            next.delete("create");
            (createForm?.form.context_params ?? []).forEach(name => next.delete(name));
            setSearchParams(next, { replace: true });
        }
    };
    const openCreation = () => {
        if (offeredForms.length > 1) setChooserOpen(true);
        else setCreateFormOpen(true);
    };
    // `?create=<form name>` opens that form directly, with its context parameters read from the same
    // URL (`?create=sales-other&source_registration_id=42`): the entry point of a creation started
    // from another screen.
    const requestedForm = searchParams.get("create");
    useEffect(() => {
        if (!requestedForm) return;
        const offered = offeredForms.find(candidate => candidate.form.name === requestedForm);
        if (!offered) return;
        setChosenForm(offered);
        setCreateFormOpenState(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedForm, props.metadata]);

    // Track what triggered the last data fetch
    const [lastTriggerType, setLastTriggerType] = useState<"view-change" | "table-interaction">("view-change");

    // Utiliser metadata passée en prop si présente
    const fieldsInfo: Record<string, any> | null = props.metadata?.actions?.POST || null;

    // Saved views are not offered to every audience (partners filter, sort and pick columns without saving).
    // While the user loads, the audience is unknown: the views are fetched, so that a standard user's pinned or
    // linked view is not replaced by the default one before the audience is known.
    const canSaveViews = offersSavedViews(audience);
    const { views, loading: viewsLoading, updateView, pinView, unpinView, saveAsView, deleteView, fetchViews } = useListViews(
        canSaveViews ? props.metadata?.content_type_id || undefined : undefined
    );

    // Column management: initialize from metadata default_columns so the first API fetch has a valid URL (avoid infinite loading when visibleColumns would otherwise be [])
    const [visibleColumns, setVisibleColumns] = useState<string[]>(() => props.metadata?.list_config?.default_columns ?? []);

    // Views management
    const { currentView, handleViewChange, columnsManuallyModified, setColumnsManuallyModified } = useObjectListViews(views, viewsLoading, props.metadata?.list_config?.default_columns);

    const isRecentlyViewed = currentView?.id === RECENTLY_VIEWED_VIEW_ID;
    // « Recently viewed » reads the recent records, a quick access not offered to every audience.
    const recentlyViewedViewForSelector = is_visible_to_audience(LAYOUT_AUDIENCES.quickAccess) && props.metadata?.content_type_id
        ? buildRecentlyViewedView(props.metadata?.list_config?.default_columns || ["id", "name", "created_at"])
        : null;

    // Track when we have a genuine view change vs other updates
    const [lastViewId, setLastViewId] = useState<string | number | null>(null);

    // Filters management
    const {
        drqFilterState,
        currentDRQQuery,
        handleSetFilterLogic,
        loadFiltersFromDRQ,
        handleAddFilter,
        handleUpdateFilter,
        handleRemoveFilter,
        handleClearAllFilters,
        markFiltersSaved,
        getActiveFilters,
        filterLogic
    } = useObjectListFilters();

    // Advanced filters (`crm.filter_tree`): when set, the list runs this tree instead of the sidebar
    // filters. Only views saved from the advanced editor open in this mode (`filter_mode`).
    const objectType = props.metadata?.object_type;
    const [advancedTree, setAdvancedTree] = useState<FilterTree | null>(null);
    // The tree open in the advanced dialog (null: closed).
    const [advancedEditorTree, setAdvancedEditorTree] = useState<FilterTree | null>(null);
    const isAdvanced = !isRecentlyViewed && advancedTree !== null;

    // Ordering management
    const [orderBy, setOrderBy] = useState<ListViewOrderBy | null>(null);

    // UI state (moved up to be available for data fetching)
    const [searchValue, setSearchValue] = useState<string>("");

    // Recently viewed record IDs (synthetic view)
    const [recentIds, setRecentIds] = useState<Array<string | number>>([]);
    const [recentLoading, setRecentLoading] = useState(false);
    const [recentError, setRecentError] = useState<string | null>(null);
    const [recentFetchKey, setRecentFetchKey] = useState(0);

    useEffect(() => {
        if (!isRecentlyViewed || !props.metadata?.content_type_id) {
            setRecentIds([]);
            setRecentLoading(false);
            setRecentError(null);
            return;
        }
        let cancelled = false;
        setRecentLoading(true);
        setRecentError(null);
        RecentRecordService.byReferenceTypeId(props.metadata.content_type_id)
            .then(records => {
                if (cancelled) return;
                setRecentIds(records.map(r => r.reference_id));
                setRecentLoading(false);
            })
            .catch(err => {
                if (cancelled) return;
                console.error(err);
                setRecentError(err?.message || "Failed to load recently viewed records");
                setRecentIds([]);
                setRecentLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isRecentlyViewed, props.metadata?.content_type_id, recentFetchKey, currentView?.id]);

    const recentDRQ = useMemo(() => DRQBuilder.filter("id__in", recentIds.length ? recentIds : []), [recentIds]);
    const effectiveDRQ = isRecentlyViewed ? recentDRQ : isAdvanced ? {} : currentDRQQuery;

    // Data fetching — wait until list views are loaded and a current view is selected so filters/columns match the view before hitting the API.
    const autoFetch = props.autoFetch ?? true;
    const blockListFetch = viewsLoading || !currentView || (isRecentlyViewed && recentLoading);
    const dataFetching = useDataFetching<T>(
        props.endpointUrl,
        visibleColumns,
        effectiveDRQ,
        autoFetch,
        blockListFetch,
        orderBy,
        searchValue,
        isRecentlyViewed || isAdvanced ? [] : drqFilterState,
        isRecentlyViewed || isAdvanced ? "" : filterLogic,
        props.metadata?.list_config?.default_columns,
        isRecentlyViewed ? 100 : undefined,
        isRecentlyViewed,
        isRecentlyViewed ? recentFetchKey : undefined,
        isAdvanced ? advancedTree : null
    );

    // Use either auto-fetched data or provided data
    const actualData = autoFetch ? dataFetching.data : props.resolvedData;
    const actualIsLoading = autoFetch ? dataFetching.isLoading : navigation.state === "loading";
    const actualAccumulatedResults = autoFetch ? dataFetching.accumulatedResults : props.resolvedData?.results || [];
    const actualError = autoFetch ? dataFetching.error : null;

    const displayResults = useMemo(() => {
        if (!isRecentlyViewed || recentIds.length === 0) return actualAccumulatedResults;
        const order = new Map(recentIds.map((id, i) => [String(id), i]));
        return [...actualAccumulatedResults].sort((a: any, b: any) => {
            const ai = order.get(String(a.id)) ?? Number.MAX_SAFE_INTEGER;
            const bi = order.get(String(b.id)) ?? Number.MAX_SAFE_INTEGER;
            return ai - bi;
        });
    }, [isRecentlyViewed, recentIds, actualAccumulatedResults]);

    // UI state
    const [selectedItems, setSelectedItems] = useState<T[]>([]);
    const [selectAll, setSelectAll] = useState<boolean>(false);

    const clearSelection = () => {
        setSelectedItems([]);
        setSelectAll(false);
    };

    const resetListData = () => {
        dataFetching.resetAccumulatedResults();
        clearSelection();
    };

    useEffect(() => {
        props.onSelectionChange?.(selectedItems);
    }, [selectedItems]); // eslint-disable-line react-hooks/exhaustive-deps
    const [viewsModalOpen, setViewsModalOpen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [columnsModalOpen, setColumnsModalOpen] = useState(false);
    const [saveAsViewModalOpen, setSaveAsViewModalOpen] = useState(false);
    const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
    const [deleteViewModalOpen, setDeleteViewModalOpen] = useState(false);
    const [shareViewModalOpen, setShareViewModalOpen] = useState(false);
    const [editingView, setEditingView] = useState<ListView | null>(null);
    const [viewToDelete, setViewToDelete] = useState<ListView | null>(null);

    // Charts attached to the current list view
    const [charts, setCharts] = useState<ListViewChart[]>([]);
    const [chartsPanelOpen, setChartsPanelOpen] = useState(false);
    // Charts refetch on filter changes only. Bump this to also refetch them
    // when the rows are reloaded with an unchanged query (Refresh, action).
    const [chartsRefreshNonce, setChartsRefreshNonce] = useState(0);
    useEffect(() => {
        setCharts((currentView?.charts as ListViewChart[]) ?? []);
    }, [currentView?.id, currentView?.charts]);

    const refetchCharts = useCallback(async () => {
        if (!currentView?.id || currentView.id === "default") return;
        try {
            const next = await ListViewService.listCharts(currentView.id as number);
            setCharts(next);
        } catch (err) {
            console.error("Failed to refetch charts", err);
        }
    }, [currentView?.id]);

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Désactiver l'espace pour la UtilityBar sur cette page
    useEffect(() => {
        setShowUtilityBarSpace(false);
        return () => setShowUtilityBarSpace(true);
    }, [setShowUtilityBarSpace]);

    // Set document title
    useEffect(() => {
        if (currentView) {
            document.title = `${currentView.name} | Walter CRM`;
        } else if (props.page_title) {
            document.title = `${props.metadata?.model_name_plural} | Walter CRM`;
        }
    }, [props.page_title, currentView]);

    // Sync visibleColumns from metadata default_columns when they become available (e.g. after lazy load) so the table shows all default columns and not just id
    const defaultColumnsFromMetadata = props.metadata?.list_config?.default_columns;
    useEffect(() => {
        if (
            defaultColumnsFromMetadata &&
            defaultColumnsFromMetadata.length > 0 &&
            !columnsManuallyModified &&
            (visibleColumns.length === 0 || (visibleColumns.length === 1 && visibleColumns[0] === "id"))
        ) {
            setVisibleColumns(defaultColumnsFromMetadata);
        }
    }, [defaultColumnsFromMetadata, columnsManuallyModified, visibleColumns.length]);

    // Handle view deletion
    const handleDeleteView = useCallback(async () => {
        if (!viewToDelete) return;

        try {
            await deleteView(viewToDelete.id as number);

            // Si la vue supprimée était la vue courante, passer à la première vue disponible ou null
            if (currentView?.id === viewToDelete.id) {
                const remainingViews = views.filter(v => v.id !== viewToDelete.id);
                if (remainingViews.length > 0) {
                    await handleViewChange(remainingViews[0]);
                } else {
                    // Réinitialiser à la vue par défaut si aucune vue n'est disponible
                    window.location.reload(); // Simple solution pour revenir à l'état initial
                }
            }

            toast.success("View deleted successfully");
        } catch (error) {
            toast.error("Error deleting view");
            console.error("Error deleting view:", error);
            throw error;
        }
    }, [viewToDelete, deleteView, currentView, views, handleViewChange]);

    // Single effect to handle view synchronization and data fetch
    useEffect(() => {
        if (!currentView) return;
        // Check if this is a genuine view change
        const isGenuineViewChange = currentView.id !== lastViewId;
        if (isGenuineViewChange) {
            setLastViewId(currentView.id || null);
            setLastTriggerType("view-change");
            resetListData();
            setAdvancedEditorTree(null);
            const opensAdvanced = currentView.filter_mode === "advanced" && !!currentView.filter_tree;
            setAdvancedTree(opensAdvanced ? currentView.filter_tree! : null);
            // The side panel cannot read filters saved by the advanced editor: it starts empty.
            const classicFilters = opensAdvanced ? {} : currentView.filters;

            if (currentView.id === RECENTLY_VIEWED_VIEW_ID) {
                // Synthetic view: clear filters (UI hidden) and force non-empty default columns
                loadFiltersFromDRQ({}, true);
                handleSetFilterLogic("");
                setOrderBy(null);
                const defaultCols = props.metadata?.list_config?.default_columns;
                setVisibleColumns(
                    defaultCols && defaultCols.length > 0 ? defaultCols : currentView.columns?.length ? currentView.columns : ["id"]
                );
            } else if (currentView.id === "default") {
                // Pour la vue par défaut, charger les filtres seulement si elle en a
                // et seulement si on n'a pas déjà des filtres locaux non sauvegardés
                if (classicFilters && Object.keys(classicFilters).length > 0) {
                    loadFiltersFromDRQ(classicFilters, true); // forceLoad = true
                } else {
                    // Si la vue par défaut n'a pas de filtres, réinitialiser les filtres
                    loadFiltersFromDRQ({}, true); // forceLoad = true
                }
                // Load filter logic from current view
                if (!opensAdvanced && (currentView as any) && (currentView as any).filter_logic !== undefined) {
                    handleSetFilterLogic((currentView as any).filter_logic || "");
                } else {
                    handleSetFilterLogic("");
                }
            } else {
                // Pour les autres vues, toujours charger les filtres
                if (classicFilters && Object.keys(classicFilters).length > 0) {
                    loadFiltersFromDRQ(classicFilters, true); // forceLoad = true
                } else {
                    loadFiltersFromDRQ({}, true); // forceLoad = true
                }
                // Load filter logic from current view
                if (!opensAdvanced && (currentView as any) && (currentView as any).filter_logic !== undefined) {
                    handleSetFilterLogic((currentView as any).filter_logic || "");
                } else {
                    handleSetFilterLogic("");
                }
            }

            // Load ordering from current view (skip for recently viewed — already cleared above)
            if (currentView.id !== RECENTLY_VIEWED_VIEW_ID) {
                if (currentView.order_by) {
                    setOrderBy({
                        column: currentView.order_by.column,
                        direction: currentView.order_by.direction,
                        origin: currentView.order_by.origin ?? null
                    });
                } else {
                    setOrderBy(null);
                }
            }
        }
        // Always sync columns with current view (only if not manually modified)
        if (currentView.id === RECENTLY_VIEWED_VIEW_ID) {
            if (!columnsManuallyModified) {
                const defaultCols = props.metadata?.list_config?.default_columns;
                setVisibleColumns(
                    defaultCols && defaultCols.length > 0 ? defaultCols : currentView.columns?.length ? currentView.columns : ["id"]
                );
            }
        } else if (!columnsManuallyModified && currentView.columns) {
            setVisibleColumns(currentView.columns);
        }
    }, [currentView, columnsManuallyModified, loadFiltersFromDRQ, lastViewId, handleSetFilterLogic, props.metadata?.list_config?.default_columns]);

    // Close filters/charts panels when entering recently viewed
    useEffect(() => {
        if (isRecentlyViewed) {
            setFiltersOpen(false);
            setChartsPanelOpen(false);
        }
    }, [isRecentlyViewed]);

    // Full-page loader: first paint only. Once the list has rendered, a view switch keeps the layout
    // mounted and surfaces loading inside the table — swapping the whole card for a spinner blanks the
    // header and selector on every change.
    const hasRenderedOnceRef = useRef(false);
    useEffect(() => {
        const blocked = viewsLoading || !currentView || (isRecentlyViewed && recentLoading);
        const noRowsYet = displayResults.length === 0;
        const listOrCountInFlight = autoFetch && (dataFetching.isLoading || dataFetching.isLoadingCount);
        const showForViewFlow = listOrCountInFlight && noRowsYet && !actualError && !recentError && lastTriggerType === "view-change";

        const showFullPageLoader = hasRenderedOnceRef.current ? false : blocked || showForViewFlow;
        if (!showFullPageLoader) {
            hasRenderedOnceRef.current = true;
        }
        setGlobalLoading(showFullPageLoader);
    }, [
        viewsLoading,
        currentView,
        autoFetch,
        dataFetching.isLoading,
        dataFetching.isLoadingCount,
        displayResults.length,
        lastTriggerType,
        actualError,
        recentError,
        isRecentlyViewed,
        recentLoading
    ]);

    // Generate columns and filters
    const allColumns = useMemo(() => mergeColumns(props.metadata?.list_config?.default_columns, fieldsInfo), [props.metadata?.list_config?.default_columns, fieldsInfo]);

    const allFilters = useMemo(() => generateAutoFilters(props.metadata), [props.metadata]);

    const displayColumns = useMemo(() => getFilteredColumns(visibleColumns, allColumns, fieldsInfo), [visibleColumns, allColumns, fieldsInfo]);
    // The columns a header click sorts on, offered first by the Sort panel.
    const sortColumns = useMemo(
        () => displayColumns.flatMap(column => (column.fieldName && !column.hidden ? [{ fieldName: column.fieldName, label: column.name }] : [])),
        [displayColumns]
    );

    // Handle pagination and infinite scroll
    const totalCount = dataFetching.count || 0;
    const totalPages = Math.ceil(totalCount / dataFetching.pageSize);
    const hasMore = dataFetching.currentPage < totalPages;

    const stableHandleLoadMore = useCallback(() => {
        if (isRecentlyViewed) return;
        if (!dataFetching.isLoadingMore && !actualIsLoading && hasMore) {
            dataFetching.handleLoadMore();
        }
    }, [isRecentlyViewed, dataFetching.isLoadingMore, actualIsLoading, hasMore, dataFetching.handleLoadMore]);

    // Enhanced view change handler
    const enhancedHandleViewChange = useCallback(
        async (view: ListView) => {
            resetListData();
            handleViewChange(view);
        },
        [handleViewChange, dataFetching.resetAccumulatedResults]
    );

    // Handlers
    const handleAutoResize = () => {
        if ((window as any).autoResizeTableColumns) {
            (window as any).autoResizeTableColumns();
        }
    };

    const handleRefresh = () => {
        setLastTriggerType("table-interaction");
        clearSelection();
        setChartsRefreshNonce(k => k + 1);
        if (isRecentlyViewed) {
            dataFetching.resetAccumulatedResults();
            setRecentFetchKey(k => k + 1);
        } else {
            dataFetching.handlePageReset();
            if (autoFetch) {
                dataFetching.resetAccumulatedResults();
            }
        }
        revalidator.revalidate();
    };

    const handleActionSuccess = () => {
        clearSelection();
        setChartsRefreshNonce(k => k + 1);
        dataFetching.handlePageReset();
    };

    const handlePinView = async (id: number) => {
        await pinView(id, true);
        toast.success("List View has been pinned");
    };

    const handleUnpinView = async (id: number) => {
        await unpinView(id, true);
        toast.success("The List View has been unpinned");
    };

    // Active filters for display
    const { activeFiltersDisplay } = getActiveFilters(allFilters, searchValue);

    // Comparaison des filtres pour l'étoile (corrigée)
    const currentViewFiltersDRQ = currentView?.filters || {};
    const currentViewFilterLogic = (currentView as any)?.filter_logic || "";
    // On sérialise l'état courant en DRQ pour comparer au format objet
    const currentFiltersDRQ = DRQBuilder.buildFromFilterInstances(drqFilterState || []);
    const currentLogic = filterLogic || "";

    const hasUnsavedFilters =
        !isRecentlyViewed &&
        ((isAdvanced
            ? !areTreesEqual(currentView?.filter_tree, advancedTree)
            : currentView?.filter_mode === "advanced" || !areDRQObjectsEqual(currentViewFiltersDRQ, currentFiltersDRQ) || currentViewFilterLogic !== currentLogic) ||
            !areArraysEqual(currentView?.columns || [], visibleColumns || []));

    // The filters a save sends: the advanced tree, or the sidebar filters (which clear any saved tree).
    const filtersToSave = (instances: typeof drqFilterState) =>
        isAdvanced ? { filters: {}, filter_tree: advancedTree } : { filters: DRQBuilder.buildFromFilterInstances(instances || []), filter_logic: filterLogic || null };

    // Opens the advanced editor on the current filters: the sidebar ones (its draft, when it hands
    // one) are read back by the server.
    const openAdvancedFilters = async (draft?: { filters: typeof drqFilterState; logic: string }) => {
        if (!objectType) return;
        if (isAdvanced) {
            setAdvancedEditorTree(advancedTree);
            return;
        }
        try {
            const tree = await DataService.client
                .url("/crm/app/filter-tree/from-filters/")
                .post({
                    model: objectType,
                    filters: DRQBuilder.buildFromFilterInstances(draft ? draft.filters : drqFilterState || []),
                    filter_logic: (draft ? draft.logic : filterLogic) || ""
                })
                .json<FilterTree>();
            setFiltersOpen(false);
            setAdvancedEditorTree(tree);
        } catch (error) {
            console.error("Error reading the filters for the advanced editor:", error);
            toast.error("These filters cannot be opened in the advanced editor");
        }
    };

    // Auto-redirect logic
    useEffect(() => {
        if (props.allowAutoRedirect && searchParams.has("autoRedirect") && displayResults.length === 1) {
            const navigateTo = props.onRowNavigateTo?.(displayResults[0], location);
            if (typeof navigateTo === "string") {
                navigate(navigateTo, { replace: true });
            }
        }
    }, [displayResults, props.allowAutoRedirect, props.onRowNavigateTo, location, searchParams]);

    // Modified paginated response for table
    const modifiedPaginatedResponse: PaginatedResponse<T> = {
        count: dataFetching.count || 0,
        //next: actualData?.next || null,
        // previous: actualData?.previous || null,
        results: displayResults
    };

    // Show loader while loading (after all hooks are initialized)
    if (globalLoading || !currentView) {
        return (
            <div className="flex h-screen w-full items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    return (
        <>
            <div className="relative flex items-stretch gap-3">
                <div className={cn("min-w-0 flex-1")}>
                    <Card className={cn(props.className, "flex max-h-[calc(100vh-2rem)] flex-col")}>
                        <CardHeader className="shrink-0 px-6 pb-2 pt-4">
                            <div className="flex items-center justify-between">
                                <div className="flex flex-row items-center gap-4 text-2xl font-bold">
                                    <div className={cn("mt-1 flex h-12 w-12 items-center justify-between rounded-md")} style={{ backgroundColor: props.metadata?.model_metadata?.color }}>
                                        {React.createElement((LucideIcons as any)[props.metadata?.model_metadata?.icon || "Circle"], {
                                            className: "mx-auto flex h-8 w-8 text-center text-white",
                                            strokeWidth: 1.5
                                        })}
                                    </div>

                                    <div className="flex flex-col">
                                        <span className="text-sm font-medium text-gray-500">{props.page_title}</span>
                                        <ViewsSelectorComponent
                                            open={viewsModalOpen}
                                            onOpenChange={setViewsModalOpen}
                                            views={views}
                                            currentView={currentView}
                                            onViewChange={enhancedHandleViewChange}
                                            onPinView={handlePinView}
                                            onUnpinView={handleUnpinView}
                                            referenceTypeId={props.metadata?.content_type_id || 0}
                                            recentlyViewedView={recentlyViewedViewForSelector}
                                            triggerElement={
                                                <span className="flex cursor-pointer items-center border-b border-b-white text-2xl text-black hover:border-b hover:border-black">
                                                    {currentView.name}
                                                    {hasUnsavedFilters && (
                                                        <SimpleTooltip message="This view is unsaved">
                                                            <sup>
                                                                <LucideIcons.Asterisk size={20} className=" text-black" />
                                                            </sup>
                                                        </SimpleTooltip>
                                                    )}
                                                    {currentView.created_by && currentView.created_by.id !== user?.id && <LucideIcons.Lock className=" ml-2 mt-1 h-4 w-4" strokeWidth={3} />}
                                                    <ChevronDown className="ml-2 inline-block h-4 w-4" />
                                                </span>
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-row items-center gap-3">
                                    {offeredForms.length > 0 && props.metadata?.permissions?.create !== "none" && (
                                        <>
                                            <div className="flex rounded-sm border shadow-sm">
                                                <Button variant="outline" className="rounded-none border-0 shadow-none first:rounded-l-sm last:rounded-r-sm" onClick={openCreation}>
                                                    <LucideIcons.Plus className="mr-1 h-4 w-4" />
                                                    {offeredForms[0].form.create_label}
                                                </Button>
                                            </div>
                                            {offeredForms.length > 1 && (
                                                <FormChooser
                                                    open={chooserOpen}
                                                    onOpenChange={setChooserOpen}
                                                    title={offeredForms[0].form.create_label}
                                                    forms={offeredForms}
                                                    onChoose={offered => {
                                                        setChooserOpen(false);
                                                        setChosenForm(offered);
                                                        setCreateFormOpenState(true);
                                                    }}
                                                />
                                            )}
                                            {createForm && (
                                                <DynamicObjectForm
                                                    key={createForm.index}
                                                    open={createFormOpen}
                                                    onOpenChange={setCreateFormOpen}
                                                    mode="create"
                                                    formConfig={createForm.form}
                                                    endpointUrl={props.endpointUrl}
                                                    formIndex={createForm.index}
                                                    context={readContextParams(createForm.form.context_params, searchParams)}
                                                    onSuccess={() => {
                                                        handleActionSuccess();
                                                        revalidator.revalidate();
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}
                                    {props.metadata?.list_actions && props.itemToId && (
                                        <Actions2
                                            actions={props.metadata?.list_actions}
                                            selectedItemIds={selectedItems.map(item => props.itemToId?.(item) ?? "")}
                                            selectAll={selectAll}
                                            selectAllCount={totalCount}
                                            endpointUrl={props.endpointUrl}
                                            action_scope={"list"}
                                            drqQuery={dataFetching.drqQuery}
                                            searchValue={searchValue}
                                            searchFields={visibleColumns}
                                            filterTree={isAdvanced ? advancedTree : null}
                                            onActionSuccess={handleActionSuccess}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex">
                                    <CardTitle></CardTitle>
                                    {props.page_subtitle && <CardDescription>{props.page_subtitle}</CardDescription>}
                                </div>
                            </div>
                        </CardHeader>

                        <CardContent className="relative flex min-h-0 flex-1 flex-col px-0 pb-4">
                            {/* Pagination info and controls */}
                            <div className="flex shrink-0 flex-row items-end justify-between px-6 pb-4">
                                <div className="pr-20 text-xs text-gray-500">
                                    {props.allowBatchSelection && selectedItems.length > 0 && (
                                        <div className="flex flex-row items-center gap-1">
                                            <span className="text-xs">
                                                {selectAll ? (
                                                    <>
                                                        {totalCount} element{totalCount > 1 && "s"} selected
                                                    </>
                                                ) : (
                                                    <>
                                                        {selectedItems.length} element{selectedItems.length > 1 && "s"} selected
                                                    </>
                                                )}
                                            </span>
                                            {!selectAll && selectedItems.length === displayResults.length && (
                                                <>
                                                    <p className="text-xs">{" • "}</p>
                                                    <span className="cursor-pointer text-xs underline underline-offset-2 hover:text-blue-500" onClick={() => setSelectAll(true)}>
                                                        Select all {totalCount} elements
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {selectedItems.length === 0 &&
                                        (actualError?.status === 403 ? (
                                            <>You don't have access to this object</>
                                        ) : dataFetching.isLoadingCount ? (
                                            <>
                                                Displaying <strong>{displayResults.length}</strong> out of{" "}
                                                <strong>
                                                    <Skeleton className="inline-block h-4 w-4" />
                                                </strong>{" "}
                                                results
                                            </>
                                        ) : totalCount > 0 ? (
                                            <>
                                                Displaying <strong>{displayResults.length}</strong> out of <strong>{totalCount}</strong> results
                                                {!isRecentlyViewed && hasMore && <span className="ml-1 text-gray-500">(auto-loading)</span>}
                                            </>
                                        ) : isRecentlyViewed ? (
                                            <>Aucun enregistrement récemment consulté</>
                                        ) : (
                                            <>No results found (0 result)</>
                                        ))}
                                    {isAdvanced && advancedTree!.root.children.length > 0 && (
                                        <>
                                            {" • "}
                                            <span className="cursor-pointer text-xs text-gray-500 hover:underline" onClick={() => setFiltersOpen(true)}>
                                                Advanced filters ({advancedTree!.root.children.length})
                                            </span>
                                        </>
                                    )}
                                    {!isRecentlyViewed && !isAdvanced && activeFiltersDisplay.length > 0 && (
                                        <>
                                            {" • "}
                                            <span className="text-xs text-gray-500">
                                                Active filters :{" "}
                                                {activeFiltersDisplay
                                                    .map(f => (
                                                        <span key={f.id} className={f.isExclude ? "text-red-600" : "text-green-600"}>
                                                            {f.isExclude ? "⊖" : "⊕"} {f.name} ({f.value})
                                                        </span>
                                                    ))
                                                    .reduce((prev, curr, index) => [prev, index > 0 ? ", " : "", curr] as any)}
                                            </span>
                                            {filterLogic && (
                                                <>
                                                    {" • "}
                                                    <span className="text-xs font-semibold text-gray-500">Active filter logic : {filterLogic}</span>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>

                                <div className="flex items-center justify-end space-x-2">
                                    <div className="flex space-x-2">
                                        <ObjectListSearch2
                                            onSearchChange={e => {
                                                setLastTriggerType("table-interaction");
                                                clearSelection();
                                                setSearchValue(e);
                                            }}
                                            initialValue={searchValue}
                                        />
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        {props.links && props.links.map((action, index) => <React.Fragment key={`action_${index}`}>{action}</React.Fragment>)}

                                        {!isRecentlyViewed && canSaveViews && (
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="outline" className="group relative m-0 p-2">
                                                        <Settings size={16} /> <ChevronDown className="ml-1 inline-block h-3 w-3" />
                                                        <div className="absolute bottom-full left-1/2 z-10 mb-2 flex -translate-x-1/2 items-center rounded bg-white px-2 py-1 text-xs font-medium text-black opacity-0 shadow-lg group-hover:opacity-100">
                                                            <div className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-white"></div>
                                                            List view controls
                                                        </div>
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem
                                                        disabled={!currentView?.id || !currentView.can_user_edit}
                                                        onClick={async () => {
                                                            try {
                                                                const updateData = {
                                                                    ...filtersToSave(drqFilterState),
                                                                    columns: visibleColumns || [],
                                                                    order_by: orderBy
                                                                };

                                                                if (currentView!.id) {
                                                                    const listView = views.find(v => v.id === currentView!.id);
                                                                    if (listView) {
                                                                        await updateView(listView.id as number, updateData);
                                                                        markFiltersSaved(); // Clear unsaved changes flag
                                                                        toast.success("List view updated");
                                                                    }
                                                                }
                                                            } catch (error) {
                                                                toast.error("Failed to update list view");
                                                                console.error("Error updating view:", error);
                                                            }
                                                        }}
                                                    >
                                                        <Save className="mr-2 h-4 w-4" />
                                                        Save
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => setSaveAsViewModalOpen(true)}>
                                                        <SaveAll className="mr-2 h-4 w-4" />
                                                        Save As
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        disabled={!currentView?.id || !currentView.can_user_edit}
                                                        onClick={() => {
                                                            if (currentView!.id) {
                                                                const listView = views.find(v => v.id === currentView!.id);
                                                                if (listView) {
                                                                    setEditingView(listView);
                                                                    setCreateEditModalOpen(true);
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Pencil className="mr-2 h-4 w-4" />
                                                        Rename
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        disabled={!currentView?.id || (currentView.created_by && currentView.created_by.id !== user?.id)}
                                                        onClick={() => {
                                                            if (currentView!.id) {
                                                                const listView = views.find(v => v.id === currentView!.id);
                                                                if (listView) {
                                                                    setViewToDelete(listView);
                                                                    setDeleteViewModalOpen(true);
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <LucideIcons.Trash2 className="mr-2 h-4 w-4" />
                                                        Delete
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        disabled={!currentView?.id || !currentView.can_user_edit}
                                                        onClick={() => {
                                                            if (currentView!.id) {
                                                                const listView = views.find(v => v.id === currentView!.id);
                                                                if (listView) {
                                                                    setShareViewModalOpen(true);
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <Share2 className="mr-2 h-4 w-4" />
                                                        Share
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        )}

                                        {!isRecentlyViewed && currentView?.id && currentView.id !== "default" && (
                                            <Button
                                                variant="outline"
                                                className="group relative m-0 p-2"
                                                onClick={() => {
                                                    setChartsPanelOpen(o => !o);
                                                    if (!chartsPanelOpen) setFiltersOpen(false);
                                                }}
                                            >
                                                <LucideIcons.BarChart3 size={16} />
                                                <div className="absolute bottom-full left-1/2 z-10 mb-2 flex -translate-x-1/2 items-center rounded bg-white px-2 py-1 text-xs font-medium text-black opacity-0 shadow-lg group-hover:opacity-100">
                                                    <div className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-white"></div>
                                                    Quick charts
                                                </div>
                                            </Button>
                                        )}

                                        {!isRecentlyViewed && objectType && (
                                            <SortControl
                                                rootModel={objectType}
                                                orderBy={orderBy}
                                                columns={sortColumns}
                                                tree={isAdvanced ? advancedTree : null}
                                                onChange={next => {
                                                    resetListData();
                                                    setLastTriggerType("table-interaction");
                                                    setOrderBy(next);
                                                }}
                                            />
                                        )}

                                        {!isRecentlyViewed && allFilters && (
                                            <Button
                                                variant="outline"
                                                className="group relative m-0 p-2"
                                                onClick={() => {
                                                    setFiltersOpen(o => !o);
                                                    if (!filtersOpen) setChartsPanelOpen(false);
                                                }}
                                            >
                                                <Funnel size={16} />
                                                <div className="absolute bottom-full left-1/2 z-10 mb-2 flex -translate-x-1/2 items-center rounded bg-white px-2 py-1 text-xs font-medium text-black opacity-0 shadow-lg group-hover:opacity-100">
                                                    <div className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-white"></div>
                                                    Filters
                                                </div>
                                            </Button>
                                        )}

                                        {!isRecentlyViewed && (
                                            <Button variant="outline" className="group relative m-0 p-2" onClick={() => setColumnsModalOpen(true)}>
                                                <Columns3Cog size={16} />
                                                <div className="absolute bottom-full left-1/2 z-10 mb-2 flex -translate-x-1/2 items-center rounded bg-white px-2 py-1 text-xs font-medium text-black opacity-0 shadow-lg group-hover:opacity-100">
                                                    <div className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-white"></div>
                                                    Change columns
                                                </div>
                                            </Button>
                                        )}
                                        <Button variant="outline" className="group relative m-0 p-2" onClick={handleRefresh}>
                                            <RotateCw size={16} />
                                            <div className="absolute bottom-full left-1/2 z-10 mb-2 flex -translate-x-1/2 items-center rounded bg-white px-2 py-1 text-xs font-medium text-black opacity-0 shadow-lg group-hover:opacity-100">
                                                <div className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-white"></div>
                                                Refresh
                                            </div>
                                        </Button>
                                        <Button variant="outline" className="group relative m-0 p-2" onClick={handleAutoResize}>
                                            <RulerDimensionLine size={16} />
                                            <div className="absolute bottom-full left-1/2 z-10 mb-2 flex -translate-x-1/2 items-center rounded bg-white px-2 py-1 text-xs font-medium text-black opacity-0 shadow-lg group-hover:opacity-100">
                                                <div className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-white"></div>
                                                Resize
                                            </div>
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className=" max-h-[calc(100dvh-270px)] min-h-0 flex-1 overflow-y-auto" ref={scrollContainerRef}>
                                {recentError && (
                                    <div className="mx-6 mb-3 flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                        <span>{recentError}</span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setRecentFetchKey(k => k + 1)}
                                        >
                                            Retry
                                        </Button>
                                    </div>
                                )}

                                {!isRecentlyViewed && currentView?.id && currentView.id !== "default" && (
                                    <div
                                        className={cn(
                                            chartsPanelOpen ? "right-0 ml-4 opacity-100" : "pointer-events-none right-[-350px] opacity-0",
                                            "absolute border border-b-0 border-r-0 bg-gray-50 p-2 transition-[opacity,right] duration-200",
                                            "z-50 h-[calc(100dvh-270px)] w-[350px] overflow-hidden"
                                        )}
                                    >
                                        <ListViewChartsPanel
                                            listViewId={currentView.id}
                                            canEdit={!!currentView.can_user_edit}
                                            charts={charts}
                                            fieldOptions={
                                                fieldsInfo
                                                    ? Object.entries(fieldsInfo)
                                                          .map(([key, info]: [string, any]) => ({
                                                              value: key,
                                                              label: info?.label || key,
                                                              type: info?.type
                                                          }))
                                                          .sort((a, b) => a.label.localeCompare(b.label))
                                                    : []
                                            }
                                            // Resolve relative date values ("today", "yesterday", ...) to
                                            // concrete ISO ranges before sending. `currentDRQQuery` is in the
                                            // storage format which keeps relative dates; the backend's
                                            // QueryDecoder can't parse those and silently drops the whole
                                            // filter -- which made charts aggregate over the entire dataset
                                            // instead of the view's filtered subset.
                                            liveQuery={isAdvanced ? null : DRQBuilder.resolveDrqQueryForApi(currentDRQQuery, drqFilterState || [], filterLogic)}
                                            liveFilterTree={isAdvanced ? advancedTree : null}
                                            isOpen={chartsPanelOpen}
                                            refreshNonce={chartsRefreshNonce}
                                            onChartsChanged={refetchCharts}
                                            onClose={() => setChartsPanelOpen(false)}
                                        />
                                    </div>
                                )}

                                {!isRecentlyViewed && (filtersOpen || allFilters.length > 0 || isAdvanced) && (
                                    <div
                                        className={cn(
                                            filtersOpen ? "right-0 ml-4 opacity-100" : "pointer-events-none right-[-350px] opacity-0",
                                            "absolute border border-b-0 border-r-0 bg-gray-50 p-2 transition-[opacity,right] duration-200",
                                            "z-50 h-[calc(100dvh-270px)] w-[350px] overflow-y-auto overscroll-contain pb-32"
                                        )}
                                    >
                                        {isAdvanced && objectType ? (
                                            <div className="flex flex-col space-y-2 p-2">
                                                <div className="mb-1.5 flex h-10 flex-row items-center justify-between">
                                                    <h3 className="font-semibold">Advanced filters</h3>
                                                    <LucideIcons.X className="cursor-pointer rounded p-0.5 hover:bg-gray-100" size={20} onClick={() => setFiltersOpen(false)} />
                                                </div>
                                                <p className="text-xs text-gray-500">Match {advancedTree!.root.conjunction === "or" ? "any" : "all"} of these filters:</p>
                                                <div className="space-y-2">
                                                    {advancedTree!.root.children.map(node => (
                                                        <div key={node.id} className="rounded-sm border bg-white p-3 text-sm">
                                                            <FilterNodeSummary model={objectType} node={node} />
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="flex flex-row justify-between pt-6 text-sm text-blue-500">
                                                    <span className="cursor-pointer hover:underline hover:underline-offset-4" onClick={() => openAdvancedFilters()}>
                                                        <Pencil className="mr-1 inline-block" size={14} />
                                                        Edit in the advanced editor
                                                    </span>
                                                    <span
                                                        className="cursor-pointer hover:underline hover:underline-offset-4"
                                                        onClick={() => {
                                                            setLastTriggerType("table-interaction");
                                                            resetListData();
                                                            setAdvancedTree(null);
                                                            handleClearAllFilters();
                                                        }}
                                                    >
                                                        <LucideIcons.Trash2 className="mr-1 inline-block" size={14} />
                                                        Clear all filters
                                                    </span>
                                                </div>
                                            </div>
                                        ) : (
                                            <ObjectListFilters2
                                                filters={allFilters}
                                                filterState={drqFilterState || []}
                                                onAddFilter={(filter: Filter, value: string, operator: string) => {
                                                    setLastTriggerType("table-interaction");
                                                    resetListData();
                                                    handleAddFilter(filter, value, operator);
                                                }}
                                                currentLogic={filterLogic}
                                                onSetLogic={(logic: string) => {
                                                    resetListData();
                                                    handleSetFilterLogic(logic);
                                                }}
                                                onUpdateFilter={(filterId: string, filter: Filter, value: string, operator: string) => {
                                                    setLastTriggerType("table-interaction");
                                                    resetListData();
                                                    handleUpdateFilter(filterId, filter, value, operator);
                                                }}
                                                onRemoveFilter={(filterId: string) => {
                                                    setLastTriggerType("table-interaction");
                                                    resetListData();
                                                    handleRemoveFilter(filterId);
                                                }}
                                                onClearAllFilters={() => {
                                                    setLastTriggerType("table-interaction");
                                                    resetListData();
                                                    handleClearAllFilters();
                                                }}
                                                onClose={() => setFiltersOpen(false)}
                                                onOpenAdvanced={objectType ? openAdvancedFilters : undefined}
                                                rootModel={objectType}
                                            />
                                        )}
                                    </div>
                                )}

                                <ObjectListTableResizable
                                    paginated_response={modifiedPaginatedResponse}
                                    columns={displayColumns}
                                    onRowNavigateTo={props.onRowNavigateTo}
                                    allowAutoRedirect={props.allowAutoRedirect}
                                    allowBatchSelection={props.allowBatchSelection}
                                    selectedItems={selectedItems}
                                    setSelectedItems={setSelectedItems}
                                    setSelectAll={setSelectAll}
                                    selectAll={selectAll}
                                    itemToId={props.itemToId}
                                    primary_column={props.primary_column}
                                    allowColumnResizing={props.allowColumnResizing}
                                    allowColumnOrdering={props.allowColumnOrdering}
                                    allowLateralScroll={props.allowLateralScroll}
                                    layout={props.layout}
                                    onAutoResize={handleAutoResize}
                                    orderBy={orderBy}
                                    onOrderByChange={newOrderBy => {
                                        resetListData();
                                        setLastTriggerType("table-interaction");
                                        setOrderBy(newOrderBy);
                                    }}
                                    hasActiveFilters={!isRecentlyViewed && (activeFiltersDisplay.length > 0 || isAdvanced)}
                                    isLoading={actualIsLoading || dataFetching.isLoadingMore || dataFetching.isLoading || globalLoading || (isRecentlyViewed && recentLoading)}
                                    error={actualError}
                                />

                                {!isRecentlyViewed && (
                                    <ObjectListPagination2
                                        paginated_response={actualData || { results: [] }}
                                        onLoadMore={stableHandleLoadMore}
                                        isLoading={dataFetching.isLoadingMore || actualIsLoading}
                                        hasMore={hasMore}
                                        scrollContainerRef={scrollContainerRef}
                                        currentPage={dataFetching.currentPage}
                                        pageSize={dataFetching.pageSize}
                                        isDataRefreshing={!globalLoading && actualIsLoading && displayResults.length > 0}
                                    />
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Modals */}
            {objectType && (
                <FilterTreeDialog
                    open={advancedEditorTree !== null && !isRecentlyViewed}
                    onOpenChange={open => !open && setAdvancedEditorTree(null)}
                    rootModel={objectType}
                    tree={advancedEditorTree}
                    countUrl={`${props.endpointUrl}count/`}
                    onApply={next => {
                        setAdvancedEditorTree(null);
                        // The same tree fetches nothing again: emptying the list would leave it empty
                        // (« No result found ») until a Refresh.
                        if (advancedTree && JSON.stringify(next) === JSON.stringify(advancedTree)) return;
                        setLastTriggerType("table-interaction");
                        resetListData();
                        if (next.root.children.length === 0) {
                            // No filter left: back to the side panel.
                            setAdvancedTree(null);
                            handleClearAllFilters();
                        } else {
                            setAdvancedTree(next);
                        }
                    }}
                />
            )}
            <ColumnConfigModal
                open={columnsModalOpen}
                onOpenChange={setColumnsModalOpen}
                defaultVisibleFields={props.metadata?.list_config?.default_columns}
                visibleColumns={visibleColumns || []}
                onVisibleColumnsChange={columns => {
                    setLastTriggerType("table-interaction");
                    setVisibleColumns(columns);
                }}
                onColumnsManuallyModified={() => {
                    setColumnsManuallyModified(true);
                    setLastTriggerType("table-interaction");
                }}
                fieldsInfo={fieldsInfo}
            />

            <SaveAsViewModal
                open={saveAsViewModalOpen}
                onOpenChange={setSaveAsViewModalOpen}
                view={currentView!}
                onViewChange={async (view: ListView) => {
                    if (!props.metadata?.content_type_id) return;

                    try {
                        // Trie alphabétique des filtres avant sauvegarde
                        const sortedFilterInstances = [...(drqFilterState || [])].sort((a, b) => a.field_name.localeCompare(b.field_name));
                        const createData: CreateListViewData = {
                            name: view.name,
                            ...filtersToSave(sortedFilterInstances),
                            columns: visibleColumns || [],
                            order_by: orderBy,
                            reference_type: props.metadata?.content_type_id
                        };

                        const newView = await saveAsView(createData);
                        markFiltersSaved(); // Clear unsaved changes flag
                        // Refetch to get full view data (can_user_edit, etc.) from server
                        const refreshedViews = await fetchViews(true);
                        const fullView = refreshedViews?.find((v: ListView) => v.id === newView.id) ?? newView;
                        await enhancedHandleViewChange(fullView);
                        toast.success("View saved successfully");
                    } catch (err) {
                        toast.error("Failed to save view");
                        console.error("Error saving view:", err);
                    }
                }}
            />

            <RenameViewModal
                open={createEditModalOpen}
                onOpenChange={setCreateEditModalOpen}
                view={editingView || undefined}
                onSave={async (data: UpdateListViewData) => {
                    if (!editingView) return;
                    try {
                        const updatedView = await updateView(editingView!.id as number, data);
                        toast.success("View renamed");
                        await enhancedHandleViewChange(updatedView);
                    } catch (err) {
                        toast.error("Failed to rename view");
                    }
                }}
            />

            <DeleteViewModal open={deleteViewModalOpen} onOpenChange={setDeleteViewModalOpen} view={viewToDelete} onDeleteConfirm={handleDeleteView} />

            <ShareViewModal
                open={shareViewModalOpen}
                onOpenChange={setShareViewModalOpen}
                view={currentView!}
                onViewUpdate={() => {
                    // Re-fetch views to update the current view
                    fetchViews(true);
                }}
            />
        </>
    );
}

export function ObjectListLayout2<T>(props: ObjectListLayout2Props<T>) {
    const location = useLocation();
    const [resetKey, setResetKey] = useState(0);
    const lastPathname = useRef(location.pathname);

    console.log("🏷️ ObjectListLayout2", props.metadata);

    // Auto-reset only when pathname changes
    useEffect(() => {
        if (location.pathname !== lastPathname.current) {
            setResetKey(prev => prev + 1);
            lastPathname.current = location.pathname;
        }
    }, [location.pathname]);

    // Otherwise, use Suspense and Await to resolve the Promise
    return (
        <React.Suspense
            fallback={
                <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-8 w-8 animate-spin" />
                </div>
            }
        >
            <Await resolve={props.metadata} errorElement={<p>An error occurred while loading the data</p>}>
                {(resolvedData: PaginatedResponse<T>) => <ObjectListContent key={resetKey} {...props} resolvedData={resolvedData} />}
            </Await>
        </React.Suspense>
    );
}
